import React from "react";
import LegalShell, { Section } from "../components/LegalShell";

const EFFECTIVE = "7 June 2026";

export default function Privacy() {
  return (
    <LegalShell title="Privacy Policy" kicker="Your data. Our duty." effective={EFFECTIVE}>
      <p>
        TrainMate ("we", "us", "our") is an Australian software product built for personal trainers. This
        policy explains what personal information we collect, why, how we protect it, and the rights you
        (and your clients) have under the <em>Privacy Act 1988 (Cth)</em> and the Australian Privacy
        Principles (APPs). We take these obligations seriously.
      </p>

      <Section heading="1. Who is the data controller?">
        <p>
          When you sign up for TrainMate as a personal trainer, <strong>you</strong> are the data controller
          for your clients' information. TrainMate acts as your data processor. We store and secure the
          information you upload on your behalf, but we do not use it for our own purposes.
        </p>
      </Section>

      <Section heading="2. What we collect">
        <p><strong>From you (the trainer):</strong> name, email, Google account identifier, profile picture,
          business name, ABN, phone number, default hourly rate, timezone (locked to Australia/Sydney), and
          currency (locked to AUD). Collected at sign-up and when you edit your Settings.</p>
        <p><strong>About your clients (uploaded by you):</strong> name, phone number, email, fitness goal,
          free-text notes, session history, and package purchase history. This information is stored
          exclusively against your trainer account and is never combined with data from other trainers.</p>
        <p><strong>Automatically:</strong> login timestamps, session cookies, browser type, IP address at
          login, and standard server logs. These are used only for security and troubleshooting and are
          rotated regularly.</p>
      </Section>

      <Section heading="3. Why we collect it">
        <p>To provide the TrainMate service: authenticating you securely, showing you your clients and
          schedule, generating GST-compliant invoices under Australian Taxation Office rules, tracking
          session packs, and (when you enable it) sending invoice PDFs to your clients by email.</p>
        <p>We do <strong>not</strong> sell your data. We do not build advertising profiles. We do not use
          your clients' contact details for marketing.</p>
      </Section>

      <Section heading="4. GST & tax record retention (ATO requirement)">
        <p>Under Australian tax law, invoice records must be retained for a minimum of <strong>five (5)
          years</strong> from the date they are issued. TrainMate retains all invoice data (including line
          items, GST amounts, ABN, client name and totals) for this period even after you delete a client,
          in order to comply. You remain able to view and export them from the Invoices tab at any time.</p>
      </Section>

      <Section heading="5. Where your data lives">
        <p>All data is hosted in secure cloud infrastructure managed by <strong>Emergent</strong>, our
          platform provider. Databases are encrypted at rest, all traffic is TLS-encrypted in transit, and
          automatic backups run on a rolling schedule (hourly for 7 days, daily for 7 days, weekly for 4
          weeks, monthly for 12 months, yearly for 1 year) with a continuous 7-day point-in-time recovery
          window.</p>
      </Section>

      <Section heading="6. Sub-processors">
        <p>We use a small number of well-known service providers to run TrainMate. They handle data only as
          strictly needed to deliver their piece of the service:</p>
        <ul className="list-disc pl-6 space-y-1 text-[#2A2623]">
          <li><strong>Emergent</strong>: hosting, database (MongoDB), authentication session exchange</li>
          <li><strong>Google</strong>: sign-in only (we receive your email, name, profile picture; we never see your Google password)</li>
          <li><strong>Resend</strong>: transactional email delivery for invoices (only when you send an invoice)</li>
        </ul>
        <p className="text-[#6B635C] text-sm">Additional sub-processors (e.g. Stripe for payments) will be
          added to this list before any new integration goes live.</p>
      </Section>

      <Section heading="7. Google user data">
        <p>When you sign in with Google, we receive the following from your Google account, through the
          <strong> openid</strong>, <strong>email</strong> and <strong>profile</strong> permissions only:
          your name, email address, profile picture, and Google account ID.</p>
        <p>This information is used only to sign you in and run your account. It is not sold. It is not
          shared except with the hosting and service providers listed in this policy. It is not used for
          advertising.</p>
        <p>To delete this information, email us at <a href="mailto:privacy@trainmate.au" className="text-[#0F6B6B] hover:underline">privacy@trainmate.au</a> or
          use the <a href="/data-deletion" className="text-[#0F6B6B] hover:underline">Data deletion</a> page. You can also remove
          TrainMate's access to your Google account at any time at <a href="https://myaccount.google.com/permissions" className="text-[#0F6B6B] hover:underline" target="_blank" rel="noreferrer">myaccount.google.com/permissions</a>.</p>
        <p>TrainMate's use and transfer of information received from Google APIs adheres to the <a href="https://developers.google.com/terms/api-services-user-data-policy" className="text-[#0F6B6B] hover:underline" target="_blank" rel="noreferrer">Google API Services User Data Policy</a>, including the Limited Use requirements.</p>
      </Section>

      <Section heading="8. Cookies">
        <p>TrainMate uses a single essential session cookie (<code className="text-[#0F6B6B] text-xs">session_token</code>)
          to keep you logged in. It is httpOnly, secure, samesite=none, and expires after 7 days. We do not
          use tracking, advertising, or analytics cookies.</p>
      </Section>

      <Section heading="9. Your rights (APPs 12 & 13)">
        <p>You may at any time:</p>
        <ul className="list-disc pl-6 space-y-1 text-[#2A2623]">
          <li>Access all information we hold about you. Most of it is visible directly in the app under Settings, Clients, Sessions, and Invoices.</li>
          <li>Correct any information. Go to Settings and edit any field.</li>
          <li>Export your data. Email us and we'll provide a JSON export within 30 days at no cost.</li>
          <li>Request deletion. See the <a href="/data-deletion" className="text-[#0F6B6B] hover:underline">Data Deletion</a> page for the exact process and timelines.</li>
          <li>Lodge a complaint with the Office of the Australian Information Commissioner (OAIC) at <a href="https://www.oaic.gov.au" className="text-[#0F6B6B] hover:underline" target="_blank" rel="noreferrer">oaic.gov.au</a>.</li>
        </ul>
      </Section>

      <Section heading="10. Security (APP 11)">
        <p>Data is encrypted at rest and in transit. Passwords are never used or stored. Sign-in is
          Google-only. Sessions rotate every 7 days. Administrative access to the production database is
          restricted to a single trainer account (the founder) via a hardcoded allowlist. Automatic backups
          run continuously.</p>
      </Section>

      <Section heading="11. International transfers">
        <p>Some sub-processors (Google, Resend) may process metadata outside Australia. Where this occurs it
          is limited to what is necessary to deliver the service, is contractually protected, and does not
          include your clients' full contact records.</p>
      </Section>

      <Section heading="12. Children">
        <p>TrainMate is not intended for anyone under 18. If you (a trainer) upload information about a
          minor client, you warrant that you have obtained the appropriate consent from the parent or legal
          guardian.</p>
      </Section>

      <Section heading="13. Changes to this policy">
        <p>We will update the <em>Effective</em> date at the top of this page whenever the policy changes.
          Material changes will be notified to you by in-app banner at least 14 days before taking effect.</p>
      </Section>

      <Section heading="14. Contact">
        <p>Privacy questions, access requests, or complaints: <a href="mailto:privacy@trainmate.au" className="text-[#0F6B6B] hover:underline" data-testid="privacy-contact-email">privacy@trainmate.au</a>. We aim
          to acknowledge within 2 business days and respond substantively within 30 days.</p>
      </Section>
    </LegalShell>
  );
}
